import { Box, Button } from "@mui/material";

import { zodResolver } from "@hookform/resolvers/zod";
import { useForm } from "react-hook-form";
import { useTranslation } from "react-i18next";
import * as z from "zod";

import { useCreateRuleRulesPostMutation } from "api";
import { TextInput, TimeCronInput } from "components/forms";
import { IRuleMultiDayRespirationRateDeviation } from "interfaces/rules.interface";

const cronSchedule = z
  .object({
    hour: z.string().default("0"),
    minute: z.string().default("0"),
    timezone: z.string().default(""),
  })
  .default({ hour: "0", minute: "0", timezone: "" });

const ruleRRDeviationSchema = z.object({
  description: z.string().default(""),
  schedule: cronSchedule,
});

type RuleRRDeviationSchema = z.infer<typeof ruleRRDeviationSchema>;

type RuleRRDeviationFormProps = {
  entityId: string;
  defaultValues: IRuleMultiDayRespirationRateDeviation | null;
  disabled: boolean;
  onClose: () => void;
};

export function RuleRRDeviationForm({
  entityId,
  defaultValues,
  disabled,
  onClose,
}: RuleRRDeviationFormProps) {
  const { t } = useTranslation();

  const [createRule] = useCreateRuleRulesPostMutation();

  const { control, handleSubmit } = useForm({
    defaultValues: ruleRRDeviationSchema.safeParse(defaultValues ?? {}).data,
    resolver: zodResolver(ruleRRDeviationSchema),
  });

  const onSubmit = async (data: RuleRRDeviationSchema) => {
    await createRule({
      ruleCreateModels: {
        type: "subject.sessions.respiration-rate-trend-deviation",
        entity_id: entityId,
        enabled: true,
        description: data.description,
        schedule: data.schedule,
      },
    });

    onClose();
  };

  return (
    <Box
      component={"form"}
      onSubmit={handleSubmit(onSubmit, console.error)}
      sx={{ display: "flex", flexDirection: "column", flexGrow: 1, gap: 2 }}
    >
      <TextInput
        control={control}
        disabled={disabled}
        fieldName={"description"}
        label={t("rule_rr_deviation_description")}
      />
      <TimeCronInput
        control={control}
        disabled={disabled}
        fieldName={"schedule"}
        label={t("time_to_check_for_RR_deviation")}
      />
      <Box sx={{ flexGrow: 1 }} />
      {!disabled && <Button type={"submit"}>{t("Create")}</Button>}
    </Box>
  );
}
